import styles from './PlaylistStats.module.css'

import { LuAudioLines } from '@react-icons/all-files/lu/LuAudioLines'
import { LuChartNoAxesColumn } from '@react-icons/all-files/lu/LuChartNoAxesColumn'
import { LuChevronDown } from '@react-icons/all-files/lu/LuChevronDown'
import { LuChevronRight } from '@react-icons/all-files/lu/LuChevronRight'
import { LuInfo } from '@react-icons/all-files/lu/LuInfo'
import { LuX } from '@react-icons/all-files/lu/LuX'
import cn from 'clsx'
import lodashSumBy from 'lodash/sumBy'
import React, { useCallback, useMemo, useState } from 'react'
import { Link as RouterLink } from 'react-router-dom'

import { IS_ARTIST } from '@disco/constants/FeatureTypes'
import { sendMixpanelEvent } from '@disco/state-management/actions/AnalyticsActions'
import { useFeatureEnabled } from '@disco/state-management/selectors/business'
import { getTrackArtwork } from '@disco/utils/tracks'

import type {
  Playlist,
  PlaylistStatsByUser,
  PlaylistTrackStats,
} from 'types/playlist'
import type { Track } from 'types/track'

import SortableHeader from 'components/common/sortable-header'
import { useTableSort } from 'components/common/sortable-header/use-table-sort'
import TruncatedText from 'components/common/truncated-text'
import {
  Avatar,
  Box,
  Dialog,
  DiscoCallout,
  Flex,
  Grid,
  IconButton,
  Link,
  Skeleton,
  Table,
  Text,
  Tooltip,
  VisuallyHidden,
} from 'components/ds'
import PricingBadge from 'components/ui/pricing-badge'

import { useDispatch } from 'hooks/use-redux'

type TAggregatedEventsEnum = 'track_downloads' | 'track_streams'

type TSortColumn =
  | 'recipient'
  | 'views'
  | 'playlist_downloads'
  | 'track_downloads'
  | 'streams'

type Props = {
  isFeatureEnabled?: boolean
  isTrial: boolean
  playlist: Playlist
  onClose?: () => void
}

const getUsername = (user: PlaylistStatsByUser['user']) =>
  user.id !== null
    ? user.name || user.email || 'Unnamed share'
    : 'Other recipients'

const getAggregatedValue = (
  { stats, track_stats }: PlaylistStatsByUser,
  aggregatedEventType: TAggregatedEventsEnum,
) =>
  track_stats[aggregatedEventType].length === 0
    ? stats[aggregatedEventType]
    : lodashSumBy(track_stats[aggregatedEventType], 'count')

export const getSortValue = (row: PlaylistStatsByUser, column: TSortColumn) => {
  switch (column) {
    case 'recipient':
      return getUsername(row.user).toLowerCase()
    case 'views':
      return row.stats.playlist_accesses
    case 'playlist_downloads':
      return row.stats.playlist_downloads
    case 'track_downloads':
      return getAggregatedValue(row, 'track_downloads')
    case 'streams':
      return getAggregatedValue(row, 'track_streams')
  }
}

const getRowKey = (row: PlaylistStatsByUser) =>
  row.user.id ?? 'other-recipients'

const MAX_VISIBLE_TRACKS = 4

export type TMergedTrackRow = {
  track: PlaylistTrackStats['track']
  downloads: number | null
  streams: number | null
}

// Combines the two separate per-track breakdown lists into one row per
// track, so downloads and streams can be shown side by side.
export const mergeTrackBreakdown = (
  downloads: PlaylistTrackStats[],
  streams: PlaylistTrackStats[],
): TMergedTrackRow[] => {
  const rows: TMergedTrackRow[] = []
  const rowByTrackId = new Map<
    PlaylistTrackStats['track']['id'],
    TMergedTrackRow
  >()

  downloads.forEach(item => {
    const row: TMergedTrackRow = {
      track: item.track,
      downloads: item.count,
      streams: null,
    }
    rowByTrackId.set(item.track.id, row)
    rows.push(row)
  })

  streams.forEach(item => {
    const existingRow = rowByTrackId.get(item.track.id)
    if (existingRow) {
      existingRow.streams = item.count
    } else {
      rows.push({ track: item.track, downloads: null, streams: item.count })
    }
  })

  return rows
}

const PlaylistStats = ({
  isFeatureEnabled,
  isTrial,
  playlist,
  onClose,
}: Props) => {
  const dispatch = useDispatch()
  const isArtist = useFeatureEnabled(IS_ARTIST)

  const { sortColumn, sortDirection, toggleSort, sortedRows } = useTableSort(
    playlist?.stats_by_user ?? null,
    getSortValue,
  )

  const [expandedRows, setExpandedRows] = useState<Set<string | number>>(
    () => new Set(),
  )

  const toggleRowExpanded = useCallback((rowKey: string | number) => {
    setExpandedRows(previous => {
      const next = new Set(previous)
      if (next.has(rowKey)) {
        next.delete(rowKey)
      } else {
        next.add(rowKey)
      }
      return next
    })
  }, [])

  const [rowsShowingAllTracks, setRowsShowingAllTracks] = useState<
    Set<string | number>
  >(() => new Set())

  const toggleShowAllTracksForRow = useCallback((rowKey: string | number) => {
    setRowsShowingAllTracks(previous => {
      const next = new Set(previous)
      if (next.has(rowKey)) {
        next.delete(rowKey)
      } else {
        next.add(rowKey)
      }
      return next
    })
  }, [])

  // Fallback lookup for the rare case the stats response doesn't carry
  // artwork for a track (e.g. older cached data): find it in the playlist's
  // own track list instead.
  const tracksById = useMemo(
    () => new Map((playlist?.tracks || []).map(track => [track.id, track])),
    [playlist?.tracks],
  )

  const getTrackForRow = useCallback(
    (track: PlaylistTrackStats['track']): Track => {
      if (track.artwork || track.artwork_sizes) return track as Track
      return (
        tracksById.get(track.id) ??
        ({ id: track.id, name: track.name } as Track)
      )
    },
    [tracksById],
  )

  const isEmpty: boolean = useMemo(() => {
    if (!playlist) return true
    const { stats_all, stats_by_user } = playlist
    if (stats_all && stats_by_user) {
      return (
        stats_by_user.length +
          stats_all.playlist_accesses +
          stats_all.playlist_downloads +
          stats_all.track_downloads +
          stats_all.track_streams ===
        0
      )
    }
    return true
  }, [playlist])

  const handleContactsLinkClick = useCallback(() => {
    dispatch(sendMixpanelEvent('Access Contacts via playlist stats CTA'))
  }, [dispatch])

  // Navigating to the contact shouldn't also expand the row behind it.
  const handleUserClick = useCallback(
    (event: React.MouseEvent) => {
      event.stopPropagation()
      dispatch(sendMixpanelEvent('Access Contacts via playlist stats contact'))
    },
    [dispatch],
  )

  // TODO(STL-1112 part 2): swap the LuAudioLines placeholder for real
  // artwork once tracks are fetched by id instead of relying on
  // playlist.tracks (which is only a partial/paginated list).
  const renderTrackArtwork = (track: PlaylistTrackStats['track']) => {
    const artworkUrl = getTrackArtwork(getTrackForRow(track))

    return (
      <div className={styles.trackArtwork}>
        {artworkUrl ? (
          <img src={artworkUrl} alt="" className={styles.trackArtworkImage} />
        ) : (
          <div className={styles.trackArtworkFallback}>
            <LuAudioLines size={14} />
          </div>
        )}
      </div>
    )
  }

  const renderTrackDetailRows = (
    row: PlaylistStatsByUser,
    rowKey: string | number,
  ) => {
    if (!isFeatureEnabled) {
      return (
        <Table.Row>
          <Table.Cell />
          <Table.Cell colSpan={5}>
            <Flex align="center" gap="2">
              <Text size="2" color="gray">
                Upgrade to see which tracks were streamed and downloaded, and
                how many times.
              </Text>
              <PricingBadge title="PLUS" />
            </Flex>
          </Table.Cell>
        </Table.Row>
      )
    }

    const tracks = mergeTrackBreakdown(
      row.track_stats.track_downloads,
      row.track_stats.track_streams,
    )

    if (tracks.length === 0) {
      return (
        <Table.Row>
          <Table.Cell />
          <Table.Cell colSpan={5}>
            <Text size="2" color="gray">
              No per-track data available.
            </Text>
          </Table.Cell>
        </Table.Row>
      )
    }

    const showAll = rowsShowingAllTracks.has(rowKey)
    const canToggle = tracks.length > MAX_VISIBLE_TRACKS
    const visibleTracks = showAll ? tracks : tracks.slice(0, MAX_VISIBLE_TRACKS)
    const remaining = tracks.length - visibleTracks.length

    return (
      <>
        {visibleTracks.map(trackRow => (
          <Table.Row key={trackRow.track.id} className={styles.trackDetailRow}>
            <Table.Cell />
            {/* Kept to the recipient column so long names truncate there
                instead of running under the metric columns. */}
            <Table.Cell>
              <Flex align="center" gap="2" minWidth="0">
                {renderTrackArtwork(trackRow.track)}
                <TruncatedText>
                  {trackRow.track.name || 'Untitled Track'}
                </TruncatedText>
              </Flex>
            </Table.Cell>
            <Table.Cell />
            <Table.Cell />
            <Table.Cell justify="end">{trackRow.downloads ?? '—'}</Table.Cell>
            <Table.Cell justify="end">{trackRow.streams ?? '—'}</Table.Cell>
          </Table.Row>
        ))}
        {canToggle && (
          <Table.Row>
            <Table.Cell />
            {/* Pinned to the frozen column on phones, so end-aligning it there
                would push the link off-screen. */}
            <Table.Cell colSpan={5} justify={{ initial: 'start', sm: 'end' }}>
              <Link asChild>
                <button
                  type="button"
                  onClick={() => toggleShowAllTracksForRow(rowKey)}
                >
                  Show {remaining} more track
                  <span>{remaining === 1 ? '' : 's'}</span>
                </button>
              </Link>
            </Table.Cell>
          </Table.Row>
        )}
      </>
    )
  }

  const renderUser = (user: PlaylistStatsByUser['user']) => {
    const username = getUsername(user)
    const initials = username
      .split(' ')
      .slice(0, 2)
      .map(w => w[0]?.toUpperCase() ?? '')
      .join('')

    const nameEl = (
      <Flex align="center" gap="1" minWidth="0" flexGrow="1">
        <TruncatedText>{username}</TruncatedText>
        {user.id === null && (
          <Tooltip content="Recipients who accessed this playlist but are not in your contacts.">
            <span className={styles.playlistStatsInfoIcon}>
              <LuInfo size={14} />
            </span>
          </Tooltip>
        )}
      </Flex>
    )

    return (
      /* The avatar sits outside the link so only the name is clickable. */
      <Flex align="center" gap="3" minWidth="0">
        <Avatar
          size="2"
          radius="full"
          fallback={initials}
          className={styles.playlistStatsUserAvatar}
        />
        {user.contact_id ? (
          <RouterLink
            className={styles.playlistStatsUserLink}
            to={`/contacts/${user.contact_id}/activity`}
            onClick={handleUserClick}
          >
            {nameEl}
          </RouterLink>
        ) : (
          nameEl
        )}
      </Flex>
    )
  }

  const renderHeader = (
    label: string,
    column: TSortColumn,
    align?: 'start' | 'end',
  ) => (
    <SortableHeader
      label={label}
      column={column}
      align={align}
      activeColumn={sortColumn}
      direction={sortDirection}
      onSort={toggleSort}
    />
  )

  const renderTable = () => {
    return (
      <Table.Root mt="3" className={styles.recipientTable}>
        <Table.Header>
          <Table.Row>
            <Table.ColumnHeaderCell />
            {renderHeader('Recipient', 'recipient', 'start')}
            {renderHeader('Views', 'views')}
            {renderHeader('Playlist dl', 'playlist_downloads')}
            {renderHeader('Track dl', 'track_downloads')}
            {renderHeader('Streams', 'streams')}
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {sortedRows.map(row => {
            const rowKey = getRowKey(row)
            const isExpanded = expandedRows.has(rowKey)

            return (
              <React.Fragment key={rowKey}>
                <Table.Row
                  className={cn(
                    styles.recipientRow,
                    isExpanded && styles.expandedRow,
                  )}
                  onClick={() => toggleRowExpanded(rowKey)}
                >
                  <Table.Cell>
                    {/* The row is the click target, but the semantics and the
                        keyboard belong on a real button: naming the row itself
                        would replace the recipient and their metrics in the
                        accessibility tree. Hidden, so the chevron stays the
                        only thing on screen; its click bubbles to the row. */}
                    <VisuallyHidden>
                      <button type="button" aria-expanded={isExpanded}>
                        {`${
                          isExpanded ? 'Collapse' : 'Expand'
                        } per-track breakdown for ${getUsername(row.user)}`}
                      </button>
                    </VisuallyHidden>
                    {isExpanded ? (
                      <LuChevronDown size={14} className={styles.expandIcon} />
                    ) : (
                      <LuChevronRight size={14} className={styles.expandIcon} />
                    )}
                  </Table.Cell>
                  <Table.Cell>{renderUser(row.user)}</Table.Cell>
                  <Table.Cell justify="end">
                    {row.stats.playlist_accesses}
                  </Table.Cell>
                  <Table.Cell justify="end">
                    {row.stats.playlist_downloads}
                  </Table.Cell>
                  <Table.Cell justify="end">
                    {getAggregatedValue(row, 'track_downloads')}
                  </Table.Cell>
                  <Table.Cell justify="end">
                    {getAggregatedValue(row, 'track_streams')}
                  </Table.Cell>
                </Table.Row>
                {isExpanded && renderTrackDetailRows(row, rowKey)}
              </React.Fragment>
            )
          })}
        </Table.Body>
      </Table.Root>
    )
  }

  const renderContents = () => {
    const { stats_all } = playlist

    if (!stats_all || isEmpty) {
      return (
        <Flex direction="column" align="center" gap="3" py="9">
          <Flex
            align="center"
            justify="center"
            className={styles.emptyIcon}
            mb="1"
          >
            <LuChartNoAxesColumn size={32} />
          </Flex>
          <Text size="3" weight="bold">
            No activity here yet
          </Text>
          <Text size="2" color="gray">
            Generate some activity by{' '}
            <Link asChild>
              <RouterLink
                to={`/explorer/playlists/${playlist.playlist_id}/share`}
              >
                sharing your list.
              </RouterLink>
            </Link>
          </Text>
        </Flex>
      )
    }

    const statCards = [
      { label: 'Page views', value: stats_all.playlist_accesses },
      { label: 'Playlist downloads', value: stats_all.playlist_downloads },
      { label: 'Track downloads', value: stats_all.track_downloads },
      { label: 'Streams', value: stats_all.track_streams },
    ]

    return (
      <>
        <Grid columns={{ initial: '2', sm: '4' }} gap="3" mb="5">
          {statCards.map(card => (
            <Box key={card.label} className={styles.statCard}>
              <Text size="2" color="gray" className={styles.statCardLabel}>
                {card.label}
              </Text>
              <Text size="7" weight="bold" className={styles.statCardValue}>
                {card.value}
              </Text>
            </Box>
          ))}
        </Grid>

        {renderTable()}
      </>
    )
  }

  const playlistName = playlist?.name || 'Untitled playlist'

  return (
    <Dialog.Root open onOpenChange={open => !open && onClose?.()}>
      <Dialog.Content maxWidth="900px" className={styles.dialogContent}>
        <div className={styles.dialogHeader}>
          <Dialog.Close className={styles.closeButton}>
            <IconButton variant="ghost" color="gray" size="2">
              <LuX size={18} />
            </IconButton>
          </Dialog.Close>
          <Text
            size="1"
            weight="bold"
            color="gray"
            className={styles.sectionLabel}
          >
            PLAYLIST STATS
          </Text>
          <Dialog.Title mt="1" mb="0">
            {playlistName}
          </Dialog.Title>
        </div>

        <div className={styles.dialogBody}>
          {(!isFeatureEnabled || isTrial) && !isArtist && (
            <Box mb="4">
              <DiscoCallout type="warning">
                {isTrial ? (
                  <>
                    Full playlist stats, including which tracks were played and
                    downloaded and how many times, are available on&nbsp;
                    <Link asChild>
                      <RouterLink to="/settings/billing">
                        Plus and Pro plans.
                      </RouterLink>
                    </Link>
                  </>
                ) : (
                  <>
                    Want more stats, like which tracks were streamed and
                    downloaded, and how many times?&nbsp;
                    <PricingBadge title="upgrade" />
                  </>
                )}
              </DiscoCallout>
            </Box>
          )}

          {playlist?.has_stats_loaded ? (
            renderContents()
          ) : (
            <>
              <Grid columns={{ initial: '2', sm: '4' }} gap="3" mb="5">
                {Array.from({ length: 4 }).map((_, i) => (
                  <Skeleton key={i} height={{ initial: '88px', sm: '120px' }} />
                ))}
              </Grid>
              <Skeleton height="var(--font-size-1)" width="120px" mb="3" />
              <Flex direction="column" gap="3">
                {Array.from({ length: 3 }).map((_, i) => (
                  <Skeleton key={i} height="40px" />
                ))}
              </Flex>
            </>
          )}
        </div>

        {playlist?.has_stats_loaded && !isEmpty && (
          <Text size="2" color="gray" className={styles.footer}>
            To manage recipients and view full activity, visit{' '}
            <Link asChild>
              <RouterLink to="/contacts/list" onClick={handleContactsLinkClick}>
                Contacts
              </RouterLink>
            </Link>
            .
          </Text>
        )}
      </Dialog.Content>
    </Dialog.Root>
  )
}

export default React.memo<Props>(PlaylistStats)
